/**
 * iter160j — Schaden-Modul UI.
 * 2026-02 — Übersichtsseite + Create-Dialog mit Muster-Auswahl,
 *          Suchbaren Comboboxen für Objekt / Einheit / Meldender.
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/constants";
import { Warning, PaperPlaneTilt, MagnifyingGlass } from "@phosphor-icons/react";
import SearchableCombobox from "@/components/SearchableCombobox";

const DAMAGE_STATUS = {
  reported: { label: "Gemeldet", cls: "bg-amber-100 text-amber-800" },
  in_progress: { label: "In Bearbeitung", cls: "bg-blue-100 text-blue-800" },
  contractor_assigned: { label: "Handwerker beauftragt", cls: "bg-indigo-100 text-indigo-800" },
  fixed: { label: "Behoben", cls: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Abgelehnt", cls: "bg-slate-200 text-slate-700" },
  closed: { label: "Abgeschlossen", cls: "bg-slate-100 text-slate-600" },
};

const DAMAGE_PRIORITY = {
  low: { label: "Niedrig", cls: "text-slate-600" },
  medium: { label: "Mittel", cls: "text-amber-700" },
  high: { label: "Hoch", cls: "text-orange-700" },
  critical: { label: "Kritisch", cls: "text-rose-700 font-semibold" },
};

export default function Schaeden() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [propertiesIndex, setPropertiesIndex] = useState({});
  const [unitsIndex, setUnitsIndex] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await api.get("/damages", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Schadensliste konnte nicht geladen werden");
    } finally { setLoading(false); }
  };

  const loadIndex = async () => {
    try {
      const [pRes, uRes] = await Promise.all([
        api.get("/properties"),
        api.get("/properties/units/all"),
      ]);
      const pmap = {};
      (pRes.data || []).forEach((p) => { pmap[p.id] = p; });
      const umap = {};
      (uRes.data || []).forEach((u) => { umap[u.id] = u; });
      setPropertiesIndex(pmap);
      setUnitsIndex(umap);
    } catch { /* index ist nur Anzeige-Hilfe */ }
  };

  useEffect(() => { load(); loadIndex(); }, [statusFilter]);

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((d) => {
      const p = propertiesIndex[d.property_id];
      const u = unitsIndex[d.unit_id];
      const hay = [
        d.damage_number, d.title, d.description,
        p?.name, p?.property_number,
        u?.unit_number,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, propertiesIndex, unitsIndex]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Schäden</h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Alle Schadensmeldungen inkl. verknüpfter Vorgänge und Textbausteine.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-44" data-testid="damage-status-filter">
              <SelectValue placeholder="Alle Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              {Object.entries(DAMAGE_STATUS).map(([k, s]) => (
                <SelectItem key={k} value={k}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={async () => {
            try { const { data } = await api.post("/damages/seed-snippets");
              toast.success(`${data.created} Textbausteine erstellt (${data.total - data.created} bereits vorhanden)`);
            } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
          }} data-testid="seed-damage-snippets">
            Textbausteine anlegen
          </Button>
          <Button onClick={() => setOpenCreate(true)} data-testid="new-damage-btn">
            + Neuer Schaden
          </Button>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen nach Schaden-Nr., Titel, Objekt, Einheit …"
            className="pl-9 h-9"
            data-testid="damage-search"
          />
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500">Lade...</div>}
      {!loading && filteredItems.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-8 text-center">
          <Warning size={40} className="mx-auto text-slate-400 mb-2" />
          <div className="text-sm text-slate-600 font-medium">
            {items.length === 0 ? "Noch keine Schadensmeldungen erfasst." : "Keine Treffer für die aktuelle Suche."}
          </div>
          {items.length === 0 && (
            <div className="text-xs text-slate-500 mt-1">Klick auf „+ Neuer Schaden" um zu starten.</div>
          )}
        </div>
      )}

      {!loading && filteredItems.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">Schaden-Nr.</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">Titel</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">Objekt / Einheit</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">Priorität</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">Status</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">Datum</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((d) => {
                const s = DAMAGE_STATUS[d.status] || DAMAGE_STATUS.reported;
                const p = DAMAGE_PRIORITY[d.priority] || DAMAGE_PRIORITY.medium;
                const prop = propertiesIndex[d.property_id];
                const unit = unitsIndex[d.unit_id];
                return (
                  <tr key={d.id}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/schaden/${d.id}`)}
                      data-testid={`damage-row-${d.id}`}>
                    <td className="px-3 py-2 font-medium tabular-nums text-slate-900">{d.damage_number}</td>
                    <td className="px-3 py-2 text-slate-800">{d.title}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {prop ? (prop.name || prop.property_number) : "—"}
                      {unit && <span className="text-slate-400"> · {unit.unit_number}</span>}
                    </td>
                    <td className={`px-3 py-2 text-xs ${p.cls}`}>{p.label}</td>
                    <td className="px-3 py-2"><Badge className={s.cls}>{s.label}</Badge></td>
                    <td className="px-3 py-2 text-xs text-slate-600 tabular-nums">{formatDate(d.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateDamageDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={(d) => {
          load();
          // Direkt auf die Detail-Seite springen — statt Dashboard.
          if (d?.id) navigate(`/schaden/${d.id}`);
        }}
      />
    </div>
  );
}

export function CreateDamageDialog({ open, onClose, onCreated, prefill = {} }) {
  const [templates, setTemplates] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [tenancies, setTenancies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactQuery, setContactQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    template_id: "", title: "", description: "",
    damage_type: "other", priority: "medium",
    property_id: prefill.property_id || "",
    unit_id: prefill.unit_id || "",
    tenancy_id: prefill.tenancy_id || "",
    reporter_contact_id: prefill.reporter_contact_id || "",
    assigned_supplier_contact_id: "",
    occurred_at: new Date().toISOString().slice(0, 10),
    tenant_liable: false,
  });

  // Reset form when opening
  useEffect(() => {
    if (!open) return;
    setForm({
      template_id: "", title: "", description: "",
      damage_type: "other", priority: "medium",
      property_id: prefill.property_id || "",
      unit_id: prefill.unit_id || "",
      tenancy_id: prefill.tenancy_id || "",
      reporter_contact_id: prefill.reporter_contact_id || "",
      assigned_supplier_contact_id: "",
      occurred_at: new Date().toISOString().slice(0, 10),
      tenant_liable: false,
    });
    api.get("/damages/templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
    api.get("/properties").then(({ data }) => setProperties(data || [])).catch(() => setProperties([]));
    // Initial contacts (aktuelle Auswahl der letzten 200)
    api.get("/contacts", { params: { limit: 200 } }).then(({ data }) => setContacts(data || [])).catch(() => setContacts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 2026-02 Bugfix — Einheiten via /properties/units/all?property_id= laden,
  // nicht /properties/{id}/units (existierte nicht → leere Liste).
  useEffect(() => {
    if (!form.property_id) { setUnits([]); return; }
    api.get("/properties/units/all", { params: { property_id: form.property_id } })
      .then(({ data }) => setUnits(data || []))
      .catch(() => setUnits([]));
  }, [form.property_id]);

  useEffect(() => {
    if (!form.unit_id) { setTenancies([]); return; }
    api.get("/tenancies", { params: { unit_id: form.unit_id, limit: 20 } })
      .then(({ data }) => setTenancies(data.items || data || []))
      .catch(() => setTenancies([]));
  }, [form.unit_id]);

  // Kontakt-Live-Suche (>=2 Zeichen)
  useEffect(() => {
    const q = contactQuery.trim();
    if (q.length < 2) return;
    const t = setTimeout(() => {
      api.get("/contacts", { params: { q, limit: 30 } })
        .then(({ data }) => setContacts(data || []))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [contactQuery]);

  const applyTemplate = (tplId) => {
    const t = templates.find((x) => x.id === tplId);
    if (!t) return;
    setForm((f) => ({ ...f,
      template_id: tplId,
      title: t.title,
      damage_type: t.damage_type,
      priority: t.priority,
      description: t.description_template,
    }));
  };

  const propertyOptions = useMemo(() => properties.map((p) => {
    const addr = p.address || {};
    const addrStr = [
      [addr.street, addr.house_number].filter(Boolean).join(" "),
      [addr.zip, addr.city].filter(Boolean).join(" "),
    ].filter(Boolean).join(", ");
    return {
      id: p.id,
      label: p.name || p.property_number || "(unbenannt)",
      sublabel: addrStr || p.property_number,
      keywords: `${p.property_number || ""} ${addrStr}`,
    };
  }), [properties]);

  const unitOptions = useMemo(() => units.map((u) => ({
    id: u.id,
    label: u.unit_number || u.name || "(Einheit)",
    sublabel: [u.floor_label || u.floor_code, u.usage_type, u.living_area ? `${u.living_area} m²` : null]
      .filter(Boolean).join(" · "),
    keywords: `${u.unit_number || ""} ${u.name || ""} ${u.floor_label || ""}`,
  })), [units]);

  const contactOptions = useMemo(() => contacts.map((c) => {
    const dn = c.display_name || c.company_name
      || `${c.first_name || ""} ${c.last_name || ""}`.trim()
      || "(unbenannt)";
    const emails = Array.isArray(c.emails)
      ? c.emails.map((e) => typeof e === "string" ? e : e.email).filter(Boolean)
      : [];
    return {
      id: c.id,
      label: dn,
      sublabel: [c.roles?.join(", "), emails[0]].filter(Boolean).join(" · "),
      keywords: `${dn} ${emails.join(" ")} ${(c.roles || []).join(" ")}`,
    };
  }), [contacts]);

  const submit = async () => {
    if (!form.property_id || !form.title) {
      toast.error("Objekt und Titel sind Pflichtfelder");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/damages", {
        title: form.title, description: form.description,
        damage_type: form.damage_type, priority: form.priority,
        property_id: form.property_id,
        unit_id: form.unit_id || null,
        tenancy_id: form.tenancy_id || null,
        reporter_contact_id: form.reporter_contact_id || null,
        assigned_supplier_contact_id: form.assigned_supplier_contact_id || null,
        occurred_at: form.occurred_at,
        tenant_liable: !!form.tenant_liable,
      });
      toast.success(`Schaden ${data.damage_number} angelegt (Vorgang ${data.ticket?.ticket_number})`);
      onClose();
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anlage fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Neuen Schaden erfassen</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Schadenmuster (optional)</Label>
            <div className="grid grid-cols-5 gap-2 mt-1">
              {templates.map((t) => (
                <button key={t.id} type="button"
                        onClick={() => applyTemplate(t.id)}
                        className={`px-2 py-3 border rounded-md text-xs text-center hover:bg-slate-50 ${
                          form.template_id === t.id ? "border-teal-600 bg-teal-50" : "border-slate-200"}`}
                        data-testid={`damage-template-${t.id}`}>
                  <div className="font-medium">{t.title.split(" ")[0]}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Objekt *</Label>
              <SearchableCombobox
                value={form.property_id}
                onChange={(v) => setForm({ ...form, property_id: v, unit_id: "", tenancy_id: "" })}
                options={propertyOptions}
                placeholder="Objekt suchen…"
                testId="damage-property"
              />
            </div>
            <div>
              <Label className="text-xs">Einheit (optional bei Gemeinschaftsschaden)</Label>
              <SearchableCombobox
                value={form.unit_id}
                onChange={(v) => setForm({ ...form, unit_id: v, tenancy_id: "" })}
                options={unitOptions}
                placeholder={form.property_id ? "Einheit suchen…" : "Zuerst Objekt wählen"}
                disabled={!form.property_id}
                allowClear
                testId="damage-unit"
                emptyText={form.property_id ? "Keine Einheiten gefunden" : "Zuerst Objekt wählen"}
              />
            </div>
          </div>

          {tenancies.length > 0 && (
            <div>
              <Label className="text-xs">Mietverhältnis (optional, für Mieter-Zurechnung)</Label>
              <Select value={form.tenancy_id || "none"} onValueChange={(v) => setForm({ ...form, tenancy_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1" data-testid="damage-tenancy"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— kein Mietverhältnis —</SelectItem>
                  {tenancies.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.contract_number} · {(t.tenant_names || []).join(", ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Meldender (optional)</Label>
              <SearchableCombobox
                value={form.reporter_contact_id}
                onChange={(v) => setForm({ ...form, reporter_contact_id: v })}
                options={contactOptions}
                placeholder="Kontakt suchen (Mieter, Eigentümer …)"
                allowClear
                testId="damage-reporter"
                emptyText="Keine Kontakte gefunden — anders suchen?"
              />
              <div className="mt-1">
                <Input
                  value={contactQuery}
                  onChange={(e) => setContactQuery(e.target.value)}
                  placeholder="Live-Suche über alle Kontakte…"
                  className="h-7 text-xs"
                  data-testid="damage-reporter-query"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Beauftragter Handwerker (optional)</Label>
              <SearchableCombobox
                value={form.assigned_supplier_contact_id}
                onChange={(v) => setForm({ ...form, assigned_supplier_contact_id: v })}
                options={contactOptions}
                placeholder="Dienstleister/Handwerker wählen"
                allowClear
                testId="damage-supplier"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Titel *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   data-testid="damage-title" placeholder="z. B. Wasserschaden Bad" />
          </div>
          <div>
            <Label className="text-xs">Beschreibung</Label>
            <Textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                      data-testid="damage-description" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Priorität</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Niedrig</SelectItem>
                  <SelectItem value="medium">Mittel</SelectItem>
                  <SelectItem value="high">Hoch</SelectItem>
                  <SelectItem value="critical">Kritisch</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Schadendatum</Label>
              <Input type="date" value={form.occurred_at}
                     onChange={(e) => setForm({ ...form, occurred_at: e.target.value })} />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={form.tenant_liable}
                       onChange={(e) => setForm({ ...form, tenant_liable: e.target.checked })}
                       data-testid="damage-tenant-liable" />
                <span>Mieter-Haftung</span>
              </label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy} data-testid="damage-submit">
            <PaperPlaneTilt size={14} className="mr-1" />
            {busy ? "Lege an…" : "Schaden anlegen (+ Vorgang)"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
